import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from '../utils/toast';
import { getMoqRules, snapQuantity, getUnitPrice } from '../utils/importMoq';

function isImportItem(item) {
  return item?.sourceType === 'DROPSHIPPING'
    || item?.importFeesAtCheckout
    || item?.fulfillmentType === 'DANGO_IMPORT'
    || String(item?.vendorName || '').toUpperCase().includes('DANGO IMPORT');
}

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('dangoCart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        return Array.isArray(parsed)
          ? parsed.map((item) => ({
            ...item,
            _id: item._id || item.id,
            quantity: snapQuantity(item, item.quantity || 1),
          }))
          : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem('dangoCart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product, qty = 1) => {
    const { moq } = getMoqRules(product);
    const quantityToAdd = snapQuantity(product, qty || moq);
    const productId = product._id || product.id;
    const stock = Number(product.stock ?? 0);
    const hasStockLimit = !isImportItem(product) && Number.isFinite(stock) && stock > 0;

    // Rupture totale pour les produits standards ; les importations peuvent être commandées même sans stock affiché.
    if (!hasStockLimit && !isImportItem(product) && stock <= 0) {
      toast.error(`"${product.name}" est en rupture de stock.`);
      return;
    }

    let toastToTrigger = null;

    setCart((prevCart) => {
      const existing = prevCart.find((item) => (item._id || item.id) === productId);

      if (existing) {
        const currentQty = existing.quantity || 0;
        const newQty = snapQuantity(product, currentQty + quantityToAdd);

        // Dépassement du stock : uniquement pour les produits avec stock réel limité.
        if (hasStockLimit && newQty > stock) {
          const addable = stock - currentQty;
          if (addable <= 0) {
            toastToTrigger = { type: 'warn', message: `Stock maximum atteint pour "${product.name}" (${stock} en stock).` };
            return prevCart;
          }
          toastToTrigger = { type: 'warn', message: `Seulement ${addable} unité(s) ajoutée(s) — stock limité à ${stock}.` };
          return prevCart.map((item) =>
            (item._id || item.id) === productId
              ? { ...item, ...product, _id: productId, quantity: stock }
              : item
          );
        }

        toastToTrigger = { type: 'info', message: `Quantité mise à jour : ${newQty}×` };
        return prevCart.map((item) =>
          (item._id || item.id) === productId
            ? { ...item, ...product, _id: productId, quantity: newQty }
            : item
        );
      }

      // Nouveau produit
      const addQty = hasStockLimit ? snapQuantity(product, Math.min(quantityToAdd, stock)) : snapQuantity(product, quantityToAdd);
      toastToTrigger = { type: 'success', message: `${product.name} ajouté au panier` };
      return [...prevCart, { ...product, _id: productId, quantity: addQty }];
    });

    // Trigger toast outside of the render phase/setCart callback
    if (toastToTrigger) {
      toast[toastToTrigger.type](toastToTrigger.message);
    }
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart((prev) => prev.filter((item) => (item._id || item.id) !== productId));
  }, []);

  const updateQuantity = useCallback((productId, newQuantity) => {
    setCart((prev) =>
      prev.map((item) => {
        if ((item._id || item.id) !== productId) return item;
        const { moq } = getMoqRules(item);
        const stock = Number(item.stock ?? 0);
        const snapped = snapQuantity(item, newQuantity);
        const hasStockLimit = !isImportItem(item) && Number.isFinite(stock) && stock > 0;
        const clamped = hasStockLimit ? Math.max(moq, Math.min(snapped, stock)) : Math.max(moq, snapped);
        return { ...item, quantity: clamped };
      })
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    localStorage.removeItem('dangoCart');
    toast.info('Panier vidé');
  }, []);

  const restoreCart = useCallback((items) => {
    setCart(items || []);
    localStorage.setItem('dangoCart', JSON.stringify(items || []));
    toast.info('Panier restauré');
  }, []);

  const cartCount = useMemo(
    () => cart.length,
    [cart]
  );

  const subtotal = useMemo(
    () =>
      cart.reduce((total, item) => {
        const itemPrice = getUnitPrice(item);
        return total + itemPrice * (Number(item.quantity) || 1);
      }, 0),
    [cart]
  );

  const savings = useMemo(
    () =>
      cart.reduce((total, item) => {
        const orig = Number(item.price || 0);
        const sale =
          Number(item.promoPrice) > 0 && Number(item.promoPrice) < orig
            ? Number(item.promoPrice)
            : Number(item.salePrice) > 0
            ? Number(item.salePrice)
            : orig;
        return total + (orig - sale) * (Number(item.quantity) || 1);
      }, 0),
    [cart]
  );

  const shipping = useMemo(() => {
    if (cart.length > 0 && cart.every(isImportItem)) return 0;
    return subtotal >= 50000 ? 0 : 2000;
  }, [cart, subtotal]);

  const total = useMemo(() => subtotal + shipping, [subtotal, shipping]);

  // Legacy compat
  const getCartTotal = useCallback(() => subtotal, [subtotal]);
  const getCartCount = useCallback(() => cartCount, [cartCount]);

  const value = useMemo(
    () => ({
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      restoreCart,
      cartCount,
      subtotal,
      savings,
      shipping,
      total,
      getCartTotal,
      getCartCount,
    }),
    [cart, addToCart, removeFromCart, updateQuantity, clearCart, restoreCart, cartCount, subtotal, savings, shipping, total, getCartTotal, getCartCount]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
